// Editor de texto enriquecido para las NOTAS de ticket (modo opcional del
// NoteEditor). Réplica funcional del RichTextEditor del CRM (TipTap): negritas,
// cursiva, subrayado, tachado, listas, enlaces, emojis y pegado de capturas.
//
// Este SPA no tiene TipTap en su dependency tree (y seguimos el criterio de
// sanitizeEmailHtml: no añadir una dependencia pesada solo para esto), así que
// se implementa con el contenteditable del navegador + document.execCommand.
// execCommand está deprecado pero es universal en los navegadores soportados
// (browserslist de producción) y emite el mismo HTML sencillo que TipTap
// (<b>/<i>/<u>/<strike>, <ul>/<ol>, <a>) que los helpers de crmRichNote y el
// sanitizador ya entienden.
//
// Controlado "hacia fuera": `value` es el HTML actual y `onChange` se dispara
// en cada edición. El contenido del editor solo se re-setea cuando `value`
// cambia desde FUERA (p.ej. al activar el modo rico con el borrador convertido)
// — nunca en el round-trip de tecleo, o el cursor saltaría en cada tecla.

import { useCallback, useEffect, useRef, useState } from "react";
import { escapeHtml } from "../../api/crmRichNote";
import { sanitizeNoteHtml } from "../../api/sanitizeEmailHtml";

interface Props {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Pegado de IMÁGENES del portapapeles (capturas): recibe los archivos y
   * devuelve los `src` a insertar (data-URIs ya validados de tamaño/número).
   * Devolver [] descarta el pegado. Las imágenes viven como data-URI MIENTRAS
   * se redacta; al guardar, el caller las extrae a adjuntos
   * (extractRichNoteContent) — el data-URI nunca se persiste.
   */
  onPasteImageFiles?: (files: File[]) => Promise<{ src: string; alt?: string }[]>;
  /** Ctrl/Cmd+Enter dentro del editor → guardar. */
  onSubmitShortcut?: () => void;
}

// Paleta de emojis del botón 😊 (curada, sin dependencia de picker externo; el
// CRM usa emoji-picker-element). Categorías más usadas en notas internas.
const EMOJIS = [
  "😀", "😁", "😂", "🤣", "😊", "😉", "😍", "😘", "😎", "🤔",
  "😅", "😢", "😭", "😡", "😱", "🥳", "😴", "🤯", "🙃", "🤗",
  "👍", "👎", "👌", "🙏", "👏", "💪", "🤝", "✌️", "🤞", "👋",
  "❤️", "🧡", "💛", "💚", "💙", "💜", "💔", "✨", "🔥", "⭐",
  "✅", "❌", "⚠️", "❗", "❓", "💯", "🔔", "🚨", "🕐", "📌",
  "📞", "📧", "📎", "📝", "📄", "📊", "💰", "💳", "🧾", "🗓️",
  "🏠", "🚗", "✈️", "🏥", "👶", "👪", "🎂", "🎉", "🎁", "☀️",
  "🌧️", "☕", "🍀", "🐶", "🐱", "🚀", "💡", "🔒", "🔑", "🧠",
];

const toolbarBtn: React.CSSProperties = {
  border: "1px solid transparent",
  background: "transparent",
  color: "#2A3F54",
  borderRadius: "0.3rem",
  minWidth: 26,
  height: 26,
  padding: "0 0.3rem",
  fontSize: ".82rem",
  lineHeight: 1,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const toolbarBtnActive: React.CSSProperties = {
  ...toolbarBtn,
  background: "#e7f1ff",
  border: "1px solid #bfdbfe",
  color: "#0069d9",
};

type MarkState = {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strike: boolean;
  ul: boolean;
  ol: boolean;
  link: boolean;
};

const EMPTY_MARKS: MarkState = {
  bold: false,
  italic: false,
  underline: false,
  strike: false,
  ul: false,
  ol: false,
  link: false,
};

export default function RichTextEditor({
  value,
  onChange,
  placeholder,
  disabled,
  onPasteImageFiles,
  onSubmitShortcut,
}: Props) {
  const editorRef = useRef<HTMLDivElement>(null);
  // Último HTML que emitimos nosotros: distingue un cambio de `value` causado
  // por nuestro propio onChange (tecleo → NO tocar el DOM) de uno externo.
  const lastEmitted = useRef<string>(value);
  const [marks, setMarks] = useState<MarkState>(EMPTY_MARKS);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [empty, setEmpty] = useState(
    () => value.replace(/<[^>]*>/g, "").trim() === "" && !/<img\b/i.test(value),
  );

  // Sincronización externa (montaje incluido).
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (value !== lastEmitted.current || el.innerHTML === "") {
      if (el.innerHTML !== value) el.innerHTML = value;
      lastEmitted.current = value;
      setEmpty(el.textContent?.trim() === "" && !el.querySelector("img"));
    }
  }, [value]);

  const emit = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const html = el.innerHTML;
    lastEmitted.current = html;
    setEmpty(el.textContent?.trim() === "" && !el.querySelector("img"));
    onChange(html);
  }, [onChange]);

  // Estado activo de las marcas bajo el cursor (para iluminar la toolbar).
  const refreshMarks = useCallback(() => {
    const el = editorRef.current;
    const sel = window.getSelection();
    if (!el || !sel || !sel.anchorNode || !el.contains(sel.anchorNode)) {
      setMarks(EMPTY_MARKS);
      return;
    }
    const anchorEl =
      sel.anchorNode instanceof Element
        ? sel.anchorNode
        : sel.anchorNode.parentElement;
    const next: MarkState = {
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      strike: document.queryCommandState("strikeThrough"),
      ul: document.queryCommandState("insertUnorderedList"),
      ol: document.queryCommandState("insertOrderedList"),
      link: anchorEl?.closest("a") != null,
    };
    // Bail-out por igualdad: selectionchange dispara en cada movimiento de
    // cursor; sin esto, cada tecla re-renderizaría el editor con marks nuevas
    // pero idénticas.
    setMarks((prev) =>
      (Object.keys(next) as (keyof MarkState)[]).every((k) => prev[k] === next[k])
        ? prev
        : next,
    );
  }, []);

  useEffect(() => {
    document.addEventListener("selectionchange", refreshMarks);
    return () => document.removeEventListener("selectionchange", refreshMarks);
  }, [refreshMarks]);

  const exec = useCallback(
    (command: string, arg?: string) => {
      editorRef.current?.focus();
      document.execCommand(command, false, arg);
      emit();
      refreshMarks();
    },
    [emit, refreshMarks],
  );

  // Enlace: con selección → createLink sobre ella; sin selección → inserta la
  // URL como texto-enlace. Los <a> resultantes se normalizan a target=_blank.
  const setLink = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const anchorEl = (() => {
      const sel = window.getSelection();
      const n = sel?.anchorNode;
      if (!n || !el.contains(n)) return null;
      return (n instanceof Element ? n : n.parentElement)?.closest("a") ?? null;
    })();
    if (anchorEl) {
      exec("unlink");
      return;
    }
    const raw = window.prompt("URL del enlace (https://…)");
    if (!raw) return;
    const url = /^https?:\/\//i.test(raw.trim()) ? raw.trim() : `https://${raw.trim()}`;
    const sel = window.getSelection();
    const collapsed = !sel || sel.isCollapsed;
    if (collapsed) {
      exec(
        "insertHTML",
        `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer nofollow">${escapeHtml(url)}</a>&nbsp;`,
      );
    } else {
      exec("createLink", url);
    }
    // createLink no pone target/rel: normaliza todos los <a> del editor.
    Array.from(el.querySelectorAll("a")).forEach((a) => {
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer nofollow");
    });
    emit();
  }, [exec, emit]);

  const insertEmoji = useCallback(
    (emoji: string) => {
      exec("insertText", emoji);
    },
    [exec],
  );

  const onPaste = useCallback(
    async (e: React.ClipboardEvent<HTMLDivElement>) => {
      const items = Array.from(e.clipboardData.items);
      const files = items
        .filter((it) => it.kind === "file" && it.type.startsWith("image/"))
        .map((it) => it.getAsFile())
        .filter((f): f is File => f != null);
      if (files.length > 0 && onPasteImageFiles) {
        e.preventDefault();
        const srcs = await onPasteImageFiles(files);
        for (const { src, alt } of srcs) {
          exec(
            "insertHTML",
            `<img src="${src}" alt="${escapeHtml(alt ?? "captura")}">`,
          );
        }
        return;
      }
      // HTML pegado de fuera: se sanitiza YA al insertar (mismo criterio que el
      // render) para que el bodyHtml persistido nunca arrastre markup peligroso
      // ni pesado. El texto plano sigue el pegado nativo.
      const html = e.clipboardData.getData("text/html");
      if (html) {
        e.preventDefault();
        exec("insertHTML", sanitizeNoteHtml(html));
      }
    },
    [onPasteImageFiles, exec],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (onSubmitShortcut && (e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        onSubmitShortcut();
      }
    },
    [onSubmitShortcut],
  );

  // Cierra la paleta de emojis al hacer clic fuera.
  const wrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!emojiOpen) return;
    const onDocClick = (ev: MouseEvent) => {
      if (!wrapRef.current?.contains(ev.target as Node)) setEmojiOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [emojiOpen]);

  // preventDefault en mousedown: el botón no debe robar el foco/selección del
  // editor (execCommand actúa sobre la selección vigente).
  const keepSelection = (e: React.MouseEvent) => e.preventDefault();

  return (
    <div
      ref={wrapRef}
      style={{
        border: "1px solid #dee2e6",
        borderRadius: "0.5rem",
        background: disabled ? "#f8f9fa" : "white",
        position: "relative",
      }}
    >
      <div
        role="toolbar"
        aria-label="Formato de la nota"
        style={{
          display: "flex",
          gap: "0.15rem",
          flexWrap: "wrap",
          alignItems: "center",
          padding: "0.3rem 0.4rem",
          borderBottom: "1px solid #e9ecef",
        }}
      >
        <button type="button" title="Negrita (Ctrl+B)" aria-label="Negrita" aria-pressed={marks.bold} onMouseDown={keepSelection} onClick={() => exec("bold")} disabled={disabled} style={marks.bold ? toolbarBtnActive : toolbarBtn}>
          <b>B</b>
        </button>
        <button type="button" title="Cursiva (Ctrl+I)" aria-label="Cursiva" aria-pressed={marks.italic} onMouseDown={keepSelection} onClick={() => exec("italic")} disabled={disabled} style={marks.italic ? toolbarBtnActive : toolbarBtn}>
          <i>I</i>
        </button>
        <button type="button" title="Subrayado (Ctrl+U)" aria-label="Subrayado" aria-pressed={marks.underline} onMouseDown={keepSelection} onClick={() => exec("underline")} disabled={disabled} style={marks.underline ? toolbarBtnActive : toolbarBtn}>
          <u>U</u>
        </button>
        <button type="button" title="Tachado" aria-label="Tachado" aria-pressed={marks.strike} onMouseDown={keepSelection} onClick={() => exec("strikeThrough")} disabled={disabled} style={marks.strike ? toolbarBtnActive : toolbarBtn}>
          <s>S</s>
        </button>
        <span style={{ width: 1, height: 18, background: "#e9ecef", margin: "0 0.2rem" }} />
        <button type="button" title="Lista de viñetas" aria-label="Lista de viñetas" aria-pressed={marks.ul} onMouseDown={keepSelection} onClick={() => exec("insertUnorderedList")} disabled={disabled} style={marks.ul ? toolbarBtnActive : toolbarBtn}>
          ≔
        </button>
        <button type="button" title="Lista numerada" aria-label="Lista numerada" aria-pressed={marks.ol} onMouseDown={keepSelection} onClick={() => exec("insertOrderedList")} disabled={disabled} style={marks.ol ? toolbarBtnActive : toolbarBtn}>
          1.
        </button>
        <span style={{ width: 1, height: 18, background: "#e9ecef", margin: "0 0.2rem" }} />
        <button type="button" title={marks.link ? "Quitar enlace" : "Insertar enlace"} aria-label={marks.link ? "Quitar enlace" : "Insertar enlace"} aria-pressed={marks.link} onMouseDown={keepSelection} onClick={setLink} disabled={disabled} style={marks.link ? toolbarBtnActive : toolbarBtn}>
          🔗
        </button>
        <button type="button" title="Insertar emoji" aria-label="Insertar emoji" aria-expanded={emojiOpen} onMouseDown={keepSelection} onClick={() => setEmojiOpen((v) => !v)} disabled={disabled} style={emojiOpen ? toolbarBtnActive : toolbarBtn}>
          😊
        </button>
      </div>

      {emojiOpen && (
        <div
          style={{
            position: "absolute",
            zIndex: 20,
            top: 38,
            right: 8,
            width: 272,
            maxHeight: 208,
            overflowY: "auto",
            background: "white",
            border: "1px solid #dee2e6",
            borderRadius: "0.5rem",
            boxShadow: "0 6px 18px rgba(0,0,0,.12)",
            padding: "0.4rem",
            display: "grid",
            gridTemplateColumns: "repeat(8, 1fr)",
            gap: "0.1rem",
          }}
        >
          {EMOJIS.map((em) => (
            <button
              key={em}
              type="button"
              onMouseDown={keepSelection}
              onClick={() => insertEmoji(em)}
              title={em}
              style={{
                border: "none",
                background: "transparent",
                fontSize: "1.05rem",
                lineHeight: 1.4,
                cursor: "pointer",
                borderRadius: "0.3rem",
                padding: "0.1rem 0",
              }}
            >
              {em}
            </button>
          ))}
        </div>
      )}

      <div
        ref={editorRef}
        className="crm-rte crm-rich-note"
        contentEditable={!disabled}
        onInput={emit}
        onPaste={onPaste}
        onKeyDown={onKeyDown}
        data-empty={empty ? "true" : "false"}
        data-placeholder={placeholder ?? ""}
        role="textbox"
        aria-multiline="true"
        aria-label={placeholder ?? "Nota con formato"}
        style={{
          minHeight: 88,
          maxHeight: 320,
          overflowY: "auto",
          padding: "0.5rem 0.75rem",
          fontSize: ".9rem",
          outline: "none",
        }}
        suppressContentEditableWarning
      />
    </div>
  );
}
